import { useEffect, useState, forwardRef } from 'react';
import { inject, observer } from 'mobx-react';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';

import APPConf from 'lib/config/common';
import { useComponentDidMount } from 'modules/core/hooks';
import { Form, ResetButton, StringInput, SubmitButton } from 'modules/core/components/forms';
import { Button } from 'modules/core/components/buttons';
import Can from 'modules/permissions/abilities/Can';

import { SERVICE_61_EXPERT_APPOINTMENT } from 'modules/core/constants/services.js';
import * as actions from '../store/actions';
import { vehicleValidationSchema } from '../validation';
import VehicleMakerSelector from 'modules/core/components/selectors/VehicleMakerSelector';
import VehicleModelSelector from 'modules/core/components/selectors/VehicleModelSelector';
import VehicleGenerationSelector from 'modules/core/components/selectors/VehicleGenerationSelector';
import VehicleModificationSelector from 'modules/core/components/selectors/VehicleModificationSelector';
import CheckboxInput from '../../core/components/forms/CheckboxInput';
// Не удаляй комментарий ниже
// Заготовка для перехода на TS
// При переходе на React 19 решил не рисковать, отдельно сделаем переход на TS
// interface VehicleFormProps {
//   initialValues?: object
//   clientId?: string
//   serviceId?: number
//   vehicleId?: string
//   readOnly?: boolean
//   showFormActions?: boolean
//   editMode?: boolean
//   data?: object
//   error?: object
//   children?: any
//   onReset?(...args: unknown[]): unknown
//   onSubmit?(...args: unknown[]): unknown
//   clearClientVehicle?(...args: unknown[]): unknown
//   getClientVehicle?(...args: unknown[]): unknown
//   resetError?(...args: unknown[]): unknown
//   createClientVehicle?(...args: unknown[]): unknown
//   deleteClientVehicle?(...args: unknown[]): unknown
//   updateClientVehicle?(...args: unknown[]): unknown
//   order?: object
//   onChange?(...args: unknown[]): unknown
//   vehicleStore?: object
//   lightMode?: boolean
//   // показываем сокращённое количество полей
//   vinRequired?: boolean
// }

// const VehicleForm = forwardRef<HTMLElement, VehicleFormProps>((props, ref) => {
const VehicleForm = forwardRef<HTMLElement>((props, ref) => {
  const {
    initialValues,
    clientId,
    vehicleId,
    serviceId,
    data,
    error,
    readOnly = false,
    showFormActions = true,
    clearClientVehicle,
    getClientVehicle,
    resetError,
    onReset,
    onSubmit,
    editMode = false,
    children,
    createClientVehicle,
    updateClientVehicle,
    deleteClientVehicle,
    order,
    onChange,
    vehicleStore,
    lightMode = false, // показываем сокращённое количество полей
    vinRequired = false,
  } = props;

  const [loading, setLoading] = useState(false);
  const disabled = loading || readOnly;
  const policiesWasChanged = () => {
    const changedPolicies = vehicleStore?.policies?.filter(v => v.created || v.updated || v.deleted) ?? [];
    return changedPolicies.length > 0;
  };

  const errorInfo = error ? (
    <div>
      <p className='text-danger'>
        <strong>{ error.message }</strong>
      </p>
      <p className='text-danger'>
        <strong>{ APPConf.error_to_email_text }</strong>
      </p>
    </div>
  ) : null;

  const saveVehicle = async (values, form) => {
    if (loading) return;
    setLoading(true);
    const castValues = vehicleValidationSchema.cast(values);

    try {
      let resp;
      if (!values?.id) {
        resp = await createClientVehicle(castValues);
      } else {
        resp = await updateClientVehicle(castValues);
      }

      toastr.success(APPConf.successHdr, APPConf.successSaveMsg);

      if (typeof onSubmit === 'function') {
        await onSubmit(resp?.payload?.clientVehicle);
      }

      form.resetForm();
    } catch (e) {
      form.setErrors(e);
      toastr.error(APPConf.errorHdr, e.message || e);
      throw e;
    } finally {
      form.setSubmitting(false);
      setLoading(false);
    }
  };

  const deleteVehicle = async (vehicleIdToDelete) => {
    setLoading(true);
    try {
      await deleteClientVehicle(vehicleIdToDelete, clientId);
      if (typeof onChange === 'function') {
        await onChange();
      }
      toastr.success(APPConf.successHdr, APPConf.successSaveMsg);
    } catch (e) {
      toastr.error(APPConf.errorHdr, e.message || e);
    } finally {
      setLoading(false);
    }
  };

  useComponentDidMount(() => {
    resetError();
  });

  useEffect(() => {
    if (vehicleId && clientId) {
      getClientVehicle({ vehicleId, clientId });
    } else {
      clearClientVehicle();
    }
  }, [vehicleId, clientId, clearClientVehicle, getClientVehicle]);

  return (
    <Form
      ref={ ref }
      initialValues={ {
        ...initialValues,
        ...data,
        clientId,
        forOrderId: order?.id,
        serviceId,
        vinRequired,
      } }
      validationSchema={ vehicleValidationSchema }
      onReset={ onReset }
      onSubmit={ saveVehicle }
      enableReinitialize
    >
      { ({ values, setFieldValue, dirty }) => (
        <>
          <Row>
            <Col lg={ 12 }>{ errorInfo }</Col>
          </Row>
          <Row>
            <Col lg={ 3 }>
              <VehicleMakerSelector
                label='Марка'
                placeholder='Марка'
                disabled={ readOnly || editMode }
                onChange={ () => {
                  setFieldValue('modelId', null);
                  setFieldValue('generationId', null);
                  setFieldValue('modificationId', null);
                } }
              />
            </Col>
            <Col lg={ 3 }>
              <VehicleModelSelector
                makeId={ values.makeId }
                label='Модель'
                placeholder='Модель'
                disabled={ disabled || !(values.makeId > 0) }
                onChange={ () => {
                  setFieldValue('generationId', null);
                  setFieldValue('modificationId', null);
                } }
              />
            </Col>
            { lightMode && (
              <Can on='order.vehicle.number' do='available' passThrough>
                { can => (
                  <>
                    <Col lg={ 3 }>
                      <div className='d-flex flex-row'>
                        <StringInput
                          name='number'
                          label='Гос.номер'
                          placeholder='Гос.номер'
                          onChange={ e => setFieldValue('number', e.target.value.toUpperCase()) }
                          maxLength={ 20 }
                          disabled={ disabled || ((!can) && editMode) }
                          rootClassName='flex-grow-1 mr-2'
                        />
                        <CheckboxInput
                          name='foreign'
                          label='Не РФ'
                          rootClassName='mb-0 mt-4 pt-3'
                          labelClassName='text-nowrap'
                          disabled={ disabled || ((!can) && editMode) }
                        />
                      </div>
                    </Col>
                    { (serviceId === SERVICE_61_EXPERT_APPOINTMENT)
                    && (
                      <Col lg={ 3 }>
                        <StringInput
                          name='vin'
                          label='VIN'
                          placeholder='VIN'
                          maxLength={ 17 }
                          onChange={ e => setFieldValue('vin', e.target.value.toUpperCase()) }
                          disabled={ disabled }
                          required={ serviceId === SERVICE_61_EXPERT_APPOINTMENT && vinRequired }
                        />
                      </Col>
                    ) }
                  </>
                ) }
              </Can>
            ) }
            { !lightMode && (
              <>
                <Col lg={ 3 }>
                  <VehicleGenerationSelector
                    modelId={ values.modelId }
                    label='Поколения'
                    placeholder='Поколения'
                    disabled={ disabled || !(values.modelId > 0) }
                    onChange={ () => {
                      setFieldValue('modificationId', null);
                    } }
                  />
                </Col>
                <Col lg={ 3 }>
                  <VehicleModificationSelector
                    generationId={ values.generationId }
                    label='Модификация'
                    placeholder='Модификация'
                    disabled={ disabled || !(values.generationId > 0) }
                  />
                </Col>
              </>
            ) }
          </Row>

          { !lightMode && (
            <>
              <Row>
                <Can on='order.vehicle.number' do='available' passThrough>
                  { can => (
                    <Col lg={ 3 }>
                      <div className='d-flex flex-row'>
                        <StringInput
                          name='number'
                          label='Гос.номер'
                          placeholder='Гос.номер'
                          onChange={ e => setFieldValue('number', e.target.value.toUpperCase()) }
                          maxLength={ 20 }
                          disabled={ disabled || ((!can) && editMode) }
                          rootClassName='flex-grow-1 mr-2'
                        />
                        <CheckboxInput
                          name='foreign'
                          label='Не РФ'
                          rootClassName='mb-0 mt-4 pt-3'
                          labelClassName='text-nowrap'
                          disabled={ disabled || ((!can) && editMode) }
                        />
                      </div>
                    </Col>
                  ) }
                </Can>
                <Col lg={ 3 }>
                  <StringInput
                    name='vin'
                    label='VIN'
                    placeholder='VIN'
                    maxLength={ 17 }
                    onChange={ e => setFieldValue('vin', e.target.value.toUpperCase()) }
                    disabled={ disabled }
                  />
                </Col>
                <Col lg={ 3 }>
                  <StringInput
                    name='stsNumber'
                    label='СТС'
                    placeholder='СТС'
                    maxLength={ 10 }
                    disabled={ disabled }
                  />
                </Col>
                <Col lg={ 3 }>
                  <StringInput
                    name='yearOfIssue'
                    label='Год выпуска'
                    placeholder='Год выпуска'
                    maxLength={ 4 }
                    disabled={ disabled }
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={ 3 }>
                  <StringInput
                    name='ptsNumber'
                    label='ПТС'
                    placeholder='ПТС'
                    maxLength={ 15 }
                    disabled={ disabled }
                  />
                </Col>
              </Row>
            </>
          ) }

          { typeof children === 'function' ? children({ loading }) : children }

          { showFormActions && (
            <Row>
              <Col lg={ 12 }>
                { clientId && values.id && (
                  <ButtonToolbar className='float-start'>
                    <Can do='permissionDelete' on='order.vehicle' passThrough>
                      { can => (
                        <Button
                          variant='danger'
                          text='Отцепить автомобиль от клиента'
                          disabled={ !can || loading }
                          onClick={ () => deleteVehicle(values.id) }
                        />
                      ) }
                    </Can>
                  </ButtonToolbar>
                ) }

                <ButtonToolbar className='float-end'>
                  <ResetButton text='Отмена' />
                  { !readOnly && (
                    <Can do={ ['permissionInsert', 'permissionUpdate'] } on={ null } passThrough>
                      { can => (
                        <SubmitButton text='Сохранить' disabled={ !can || loading || !(dirty || policiesWasChanged()) } />
                      ) }
                    </Can>
                  ) }
                </ButtonToolbar>
              </Col>
            </Row>
          ) }
        </>
      ) }
    </Form>
  );
});

const mapStateToProps = state => ({
  data: state.vehicles?.clientVehicle,
  error: state.vehicles?.error,
  order: state.orders.item,
});

const mapDispatchToProps = {
  createClientVehicle: actions.createClientVehicle,
  clearClientVehicle: actions.clearClientVehicle,
  getClientVehicle: actions.getClientVehicle,
  resetError: actions.resetError,
  deleteClientVehicle: actions.deleteClientVehicle,
  updateClientVehicle: actions.updateClientVehicle,
};

const options = { forwardRef: true };

export default inject('vehicleStore')(observer(connect(mapStateToProps, mapDispatchToProps, null, options)(VehicleForm)));
